import React, {useState, useEffect, useRef, useCallback, useContext} from 'react';
import {useLocation} from 'react-router-dom';
import {Check, Copy, Crown, Diamond, Download, Gamepad2, Plus} from 'lucide-react';
import styles from './Account.module.css';
import {PRISMAX_BACKEND_URL, PRISMAX_DATA_PIPELINE_URL} from '../../configs/config';

// Import avatar images
import defaultAvatar from '../../static_files/badge_explorer.svg';
import amplifierAvatar from '../../static_files/badge_amplifier.svg';
import innovatorAvatar from '../../static_files/badge_innovator.svg';
import disconnectOrangeTopaz from '../../static_files/disconnect-orange-topaz.svg';
import disconnectOrangeCamel from '../../static_files/disconnect-orange-camel.svg';

import UpgradeMembershipModal from "../Data/Fleet/UpgradeMembershipModal";
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import {shortenAddress, useIsMobile} from "../../utils/utils";
import {useRecaptcha} from '../../utils/useRecaptcha';
import {useAuth} from '../../context/AuthContext';
import {useDiscordOAuth2} from './useDiscordOAuth2';
import {useTwitterOAuth2} from './useTwitterOAuth2';
import OperatorSection from './OperatorSection/OperatorSection';
import {getLinkedWalletChainFromUserData} from '../../utils/walletChain';
import {getAccountRoleFromUserData, getQaRoleFromUserData} from '../../utils/userRoles';

// Chain/email icons
import solanaChainIcon from '../../static_files/solana-chain.svg';
import ethChainIcon from '../../static_files/eth-chain.svg';
import baseChainIcon from '../../static_files/coinbase-chain.svg';
import monadChainIcon from '../../static_files/monad-chain.svg';
import aptosChainIcon from '../../static_files/aptos-chain-white.svg';
import emailIcon from '../../static_files/account-profile-email-icon.svg'

import TooltipBottom from "../Tooltip/TooltipBottom";
import EmailVerificationModal from "./EmailVerificationModal";

const normalizeEmailValue = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed === '--') {
        return '';
    }

    return trimmed.toLowerCase();
};

const TwitterXIcon = ({className}) => (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
            fill="currentColor"
            d="M17.53 3h3.17l-6.93 7.92L21.92 21h-6.38l-5-6.53L4.82 21H1.64l7.41-8.47L1.24 3h6.54l4.51 5.96L17.53 3Zm-1.11 16.23h1.76L6.83 4.68H4.94l11.48 14.55Z"
        />
    </svg>
);

const DiscordIcon = ({className}) => (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
            fill="currentColor"
            d="M19.54 5.03A16.76 16.76 0 0 0 15.4 3.75c-.18.32-.39.75-.53 1.09a15.61 15.61 0 0 0-4.74 0c-.14-.34-.36-.77-.54-1.09-1.46.25-2.86.69-4.14 1.28C2.83 8.94 2.12 12.75 2.47 16.5a16.7 16.7 0 0 0 5.07 2.56c.41-.56.77-1.15 1.08-1.78-.59-.22-1.16-.49-1.69-.81.14-.1.28-.21.41-.32a12 12 0 0 0 10.32 0c.13.11.27.22.41.32-.54.32-1.1.59-1.7.81.31.63.67 1.22 1.08 1.78a16.65 16.65 0 0 0 5.08-2.56c.41-4.34-.69-8.11-2.99-11.47ZM9.27 14.2c-.99 0-1.8-.9-1.8-2s.79-2 1.8-2 1.82.9 1.8 2c0 1.1-.8 2-1.8 2Zm5.46 0c-.99 0-1.8-.9-1.8-2s.79-2 1.8-2 1.82.9 1.8 2c0 1.1-.79 2-1.8 2Z"
        />
    </svg>
);

const TablerIcon = ({className, children}) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

const UsersGroupIcon = ({className}) => (
    <TablerIcon className={className}>
        <path d="M10 13a4 4 0 1 0 0 -8a4 4 0 0 0 0 8"/>
        <path d="M8 21h8a2 2 0 0 0 2 -2a4 4 0 0 0 -4 -4h-8a4 4 0 0 0 -4 4a2 2 0 0 0 2 2"/>
        <path d="M15 5a4 4 0 0 1 0 8"/>
        <path d="M17 15a4 4 0 0 1 4 4a2 2 0 0 1 -2 2h-1.5"/>
    </TablerIcon>
);

const ToolIcon = ({className}) => (
    <TablerIcon className={className}>
        <path d="M7 10h3v-3l-3.5 -3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1 -3 3l-6 -6a6 6 0 0 1 -8 -8l3.5 3.5"/>
    </TablerIcon>
);

const KeyIcon = ({className}) => (
    <TablerIcon className={className}>
        <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558h-4v-4l6.558 -6.558l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0z"/>
        <path d="M15 9h.01"/>
    </TablerIcon>
);

const Account = () => {
    const location = useLocation();
    const {isRecaptchaReady, executeRecaptchaAction} = useRecaptcha();

    const [userInfo, setUserInfo] = useState({
        userName: '',
        email: '',
        telegramId: '',
        twitterId: '',
        twitterName: '',
        discordId: '',
        discordName: '',
        phoneNumber: ''
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedInfo, setEditedInfo] = useState({
        userName: '',
        email: '',
        telegramId: '',
        twitterId: '',
        twitterName: '',
        discordId: '',
        discordName: '',
        phoneNumber: ''
    });
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    const [isHoveredDisconnectWallet, setIsHoveredDisconnectWallet] = useState(false);
    const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
    const [emailToVerify, setEmailToVerify] = useState('');
    const [isEmailLogin, setIsEmailLogin] = useState(() => localStorage.getItem('emailLogin') === 'true');
    const [userRoleValue, setUserRoleValue] = useState(() => localStorage.getItem('userRole') || '');
    const [uploadApiKeys, setUploadApiKeys] = useState([]);
    const [downloadApiKeys, setDownloadApiKeys] = useState([]);
    const [isLoadingUploadApiKeys, setIsLoadingUploadApiKeys] = useState(false);
    const [isLoadingDownloadApiKeys, setIsLoadingDownloadApiKeys] = useState(false);
    const [isCreatingUploadApiKey, setIsCreatingUploadApiKey] = useState(false);
    const [isCreatingDownloadApiKey, setIsCreatingDownloadApiKey] = useState(false);
    const [revokingUploadApiKeyId, setRevokingUploadApiKeyId] = useState(null);
    const [revokingDownloadApiKeyId, setRevokingDownloadApiKeyId] = useState(null);
    const [newUploadApiKey, setNewUploadApiKey] = useState('');
    const [newDownloadApiKey, setNewDownloadApiKey] = useState('');
    const shouldOpenOperatorRegisterModal = location.state?.openOperatorRegisterModal
        || new URLSearchParams(location.search).get('open') === 'register-robot';

    // Refs for input fields
    const userNameRef = useRef(null);
    const emailRef = useRef(null);
    const telegramRef = useRef(null);
    const phoneRef = useRef(null);
    const initialEmailDuringEditRef = useRef('');
    const {
        userClass,
        setUserClass,
        setUserRole,
        setQaRole,
        walletAddress,
        selectedChain,
        setSelectedChain,
        setLinkedWalletChain,
        showNotification,
        userId,
        setUserId,
        userEmail,
        gatewayToken
    } = useContext(SwapMainAppContext);
    const lastFetchedUserIdRef = useRef(userId);
    const hasFetchedUploadApiKeysRef = useRef(false);
    const hasFetchedDownloadApiKeysRef = useRef(false);
    const lastApiKeySessionRef = useRef('');
    const {
        disconnect
    } = useAuth();

    const isSmallMobile = useIsMobile(480);

    const formatDisplayValue = useCallback((value) => {
        if (value === null || value === undefined || value === '') {
            return '--';
        }
        return value;
    }, []);

    const formatEditValue = useCallback((value) => {
        if (value === null || value === undefined || value === '--') {
            return '';
        }
        return value;
    }, []);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const fetchUserInfo = useCallback(async () => {
        // Use this variable to handle the fallback case in the finally block
        let fetchSuccess = false;
        // Get user identifiers and token
        const userEmail = localStorage.getItem('userEmail') || '';
        const walletAddress = localStorage.getItem('walletAddress');
        const token = localStorage.getItem('gatewayToken');
        const emailLoginFlag = localStorage.getItem('emailLogin') === 'true';
        
        try {
            setIsLoading(true);

            if (!userEmail && !walletAddress) {
                setIsLoading(false);
                return;
            }

            setIsEmailLogin(emailLoginFlag);

            if (!token) {
                console.error('No authentication token found');
                setIsLoading(false);
                return;
            }

            // Generate a reCAPTCHA token to include in fetch request
            if (!isRecaptchaReady) {
                console.error('reCAPTCHA is not ready to fetch user info. Defaulting to fallback user info.');
                return;
            }
            const recaptchaToken = await executeRecaptchaAction('fetch_user_info');

            // Build query params
            const params = new URLSearchParams();
            if (userEmail) {
                params.append('email', userEmail);
            } else if (walletAddress) {
                params.append('wallet_address', walletAddress);
            }
            params.append('chain', selectedChain || 'solana');
            params.append('recaptcha_token', recaptchaToken);

            // Fetch user info from backend
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/get-users?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    const data = result.data;
                    const userClassType = data.user_class || 'Explorer Member';
                    const userRoleType = getAccountRoleFromUserData(data);
                    const qaRoleType = getQaRoleFromUserData(data);
                    const profileEmail = data.user_profile_email ?? '';
                    if (data.linked_wallet_address) {
                        const nextLinkedWalletChain = getLinkedWalletChainFromUserData(data);
                        localStorage.setItem('walletAddress', data.linked_wallet_address);
                        if (nextLinkedWalletChain) {
                            localStorage.setItem('linkedWalletChain', nextLinkedWalletChain);
                            localStorage.setItem('selectedChain', nextLinkedWalletChain);
                            setLinkedWalletChain?.(nextLinkedWalletChain);
                            setSelectedChain?.(nextLinkedWalletChain);
                        }
                    }

                    if (data.userid) {
                        const numericUserId = typeof data.userid === 'number'
                            ? data.userid
                            : parseInt(data.userid, 10);
                        if (!Number.isNaN(numericUserId)) {
                            lastFetchedUserIdRef.current = numericUserId;
                            setUserId(numericUserId);
                            localStorage.setItem('userId', numericUserId);
                        }
                    }

                    setUserInfo({
                        userName: formatDisplayValue(data.user_name),
                        email: formatDisplayValue(emailLoginFlag ? userEmail : profileEmail),
                        telegramId: formatDisplayValue(data.telegram_id),
                        twitterId: formatDisplayValue(data.user_profile_twitter_id),
                        twitterName: formatDisplayValue(data.user_profile_twitter_name),
                        discordId: formatDisplayValue(data.user_profile_discord_id),
                        discordName: formatDisplayValue(data.user_profile_discord_name),
                        phoneNumber: formatDisplayValue(data.phone_number)
                    });
                    setUserClass(userClassType);
                    setUserRoleValue(userRoleType);
                    setUserRole(userRoleType);
                    setQaRole(qaRoleType);
                    if (userRoleType) localStorage.setItem('userRole', userRoleType);
                    else localStorage.removeItem('userRole');
                    if (qaRoleType) localStorage.setItem('qaRole', qaRoleType);
                    else localStorage.removeItem('qaRole');
                    setEditedInfo({
                        userName: formatEditValue(data.user_name),
                        email: formatEditValue(emailLoginFlag ? userEmail : profileEmail),
                        telegramId: formatEditValue(data.telegram_id),
                        twitterId: formatEditValue(data.user_profile_twitter_id),
                        twitterName: formatEditValue(data.user_profile_twitter_name),
                        discordId: formatEditValue(data.user_profile_discord_id),
                        discordName: formatEditValue(data.user_profile_discord_name),
                        phoneNumber: formatEditValue(data.phone_number)
                    });
                    // Mark the successful fetch
                    console.log('Successfully fetched user info');
                    fetchSuccess = true;
                    setHasFetchedOnce(true);
                }
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        } finally {
            if (!fetchSuccess) {
                setIsEmailLogin(emailLoginFlag);

                const fallbackUserInfo = {
                    userName: '--',
                    email: formatDisplayValue(userEmail),
                    telegramId: '--',
                    twitterId: '--',
                    twitterName: '--',
                    phoneNumber: '--'
                };
                setUserInfo(fallbackUserInfo);
                setEditedInfo({
                    userName: '',
                    email: formatEditValue(userEmail),
                    telegramId: '',
                    twitterId: '',
                    twitterName: '',
                    phoneNumber: ''
                });
            }
            setIsLoading(false);
        }
    }, [executeRecaptchaAction, formatDisplayValue, formatEditValue, isRecaptchaReady, selectedChain, setLinkedWalletChain, setQaRole, setSelectedChain, setUserClass, setUserId, setUserRole]);

    const normalizedUserRole = (userRoleValue || '').toLowerCase();
    const isOperator = normalizedUserRole === 'operator';
    const isAdmin = normalizedUserRole === 'admin';
    const hasAuthenticatedSession = Boolean(gatewayToken && (userEmail || walletAddress));
    const apiKeySessionKey = hasAuthenticatedSession
        ? `${gatewayToken}:${userEmail || walletAddress}:${userId || ''}`
        : '';
    const canManageUploadApiKeys = hasAuthenticatedSession && isOperator;
    const canManageDownloadApiKeys = hasAuthenticatedSession && (isOperator || isAdmin);

    const getUploadApiKeyErrorMessage = useCallback(async (response, fallback) => {
        const result = await response.json().catch(() => ({}));
        return result?.msg || result?.error || fallback;
    }, []);

    const fetchUploadApiKeys = useCallback(async () => {
        const requestSessionKey = apiKeySessionKey;
        if (!gatewayToken || !requestSessionKey) {
            return;
        }

        try {
            setIsLoadingUploadApiKeys(true);
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/upload-api-keys`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${gatewayToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Failed to load upload API keys.');
            }

            if (lastApiKeySessionRef.current !== requestSessionKey) {
                return;
            }
            setUploadApiKeys(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            if (lastApiKeySessionRef.current !== requestSessionKey) {
                return;
            }
            console.error('Error fetching upload API keys:', error);
            showNotification('error', error.message || 'Failed to load upload API keys.');
        } finally {
            if (lastApiKeySessionRef.current === requestSessionKey) {
                setIsLoadingUploadApiKeys(false);
            }
        }
    }, [apiKeySessionKey, gatewayToken, showNotification]);

    const fetchDownloadApiKeys = useCallback(async () => {
        const requestSessionKey = apiKeySessionKey;
        if (!gatewayToken || !requestSessionKey) {
            return;
        }

        try {
            setIsLoadingDownloadApiKeys(true);
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/api-keys`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${gatewayToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Failed to load download API keys.');
            }

            if (lastApiKeySessionRef.current !== requestSessionKey) {
                return;
            }
            setDownloadApiKeys(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            if (lastApiKeySessionRef.current !== requestSessionKey) {
                return;
            }
            console.error('Error fetching download API keys:', error);
            showNotification('error', error.message || 'Failed to load download API keys.');
        } finally {
            if (lastApiKeySessionRef.current === requestSessionKey) {
                setIsLoadingDownloadApiKeys(false);
            }
        }
    }, [apiKeySessionKey, gatewayToken, showNotification]);
    
    // OAuth2 account linkage for Twitter and Discord
    const { handleTwitterLink, handleTwitterUnlink, isLinkingTwitter } = useTwitterOAuth2({
        fetchUserInfo,
        setUserInfo,
        setEditedInfo,
        userInfo,
        editedInfo,
        formatDisplayValue,
        formatEditValue,
    });
    const { handleDiscordLink, handleDiscordUnlink, isLinkingDiscord } = useDiscordOAuth2({
        fetchUserInfo,
        setUserInfo,
        setEditedInfo,
        userInfo,
        editedInfo,
        formatDisplayValue,
        formatEditValue,
    });

    useEffect(() => {
        // Fetch the user info once on page load when the reCAPTCHA is ready
        if (!hasFetchedOnce) {
            fetchUserInfo();
        }
    }, [isRecaptchaReady, hasFetchedOnce, fetchUserInfo]);

    useEffect(() => {
        // Show a notification for successful wallet disconnect on page reload
        const walletDisconnectSuccess = localStorage.getItem('walletDisconnectSuccess') ?? null;
        if (walletDisconnectSuccess === 'true') {
            showNotification('success', 'Your wallet has been successfully unlinked from your email.');
            localStorage.removeItem('walletDisconnectSuccess')
        }
    }, []);

    useEffect(() => {
        if (!isEditing) {
            initialEmailDuringEditRef.current = normalizeEmailValue(userInfo.email);
        }
    }, [isEditing, userInfo.email]);

    useEffect(() => {
        if (!isRecaptchaReady) {
            return;
        }

        if (lastFetchedUserIdRef.current === userId) {
            return;
        }

        lastFetchedUserIdRef.current = userId;
        fetchUserInfo();
    }, [fetchUserInfo, isRecaptchaReady, userId]);

    const saveUserInfo = useCallback(async (overrideValues = {}) => {
        const storedEmail = localStorage.getItem('userEmail') || '';
        const storedWalletAddress = localStorage.getItem('walletAddress') || '';
        const token = localStorage.getItem('gatewayToken');

        if (!token) {
            console.error('No authentication token found');
            showNotification('error', 'Authentication token missing. Please log in again.');
            return false;
        }

        const nextInfo = {
            ...editedInfo,
            ...overrideValues
        };

        const sanitizedInfo = {
            userName: nextInfo.userName?.trim() || '',
            email: nextInfo.email?.trim() || '',
            telegramId: nextInfo.telegramId?.trim() || '',
            twitterId: nextInfo.twitterId?.trim() || '',
            twitterName: nextInfo.twitterName?.trim() || '',
            phoneNumber: nextInfo.phoneNumber?.trim() || '',
            discordId: nextInfo.discordId?.trim() || '',
            discordName: nextInfo.discordName?.trim() || '',
        };

        const payload = {
            ...(storedEmail ? {email: storedEmail} : {wallet_address: storedWalletAddress}),
            user_name: sanitizedInfo.userName,
            telegram_id: sanitizedInfo.telegramId,
            phone_number: sanitizedInfo.phoneNumber,
            chain: (selectedChain || 'solana')
        };

        if (Object.prototype.hasOwnProperty.call(overrideValues, 'twitterId')) {
            payload.twitter_id = sanitizedInfo.twitterId;
        }
        if (Object.prototype.hasOwnProperty.call(overrideValues, 'twitterName')) {
            payload.twitter_name = sanitizedInfo.twitterName;
        }
        if (Object.prototype.hasOwnProperty.call(overrideValues, 'discordId')) {
            payload.discord_id = sanitizedInfo.discordId;
        }
        if (Object.prototype.hasOwnProperty.call(overrideValues, 'discordName')) {
            payload.discord_name = sanitizedInfo.discordName;
        }

        if (sanitizedInfo.email) {
            payload.user_profile_email = sanitizedInfo.email.toLowerCase();
        }

        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/update-user-info`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json().catch(() => ({}));

            if (response.ok && result.success) {
                const updatedEmailValue = payload.user_profile_email
                    ? payload.user_profile_email
                    : (userInfo.email === '--' ? '' : formatEditValue(userInfo.email));

                setUserInfo({
                    userName: formatDisplayValue(sanitizedInfo.userName),
                    email: formatDisplayValue(updatedEmailValue),
                    telegramId: formatDisplayValue(sanitizedInfo.telegramId),
                    twitterId: formatDisplayValue(sanitizedInfo.twitterId),
                    twitterName: formatDisplayValue(sanitizedInfo.twitterName),
                    discordId: formatDisplayValue(sanitizedInfo.discordId),
                    discordName: formatDisplayValue(sanitizedInfo.discordName),
                    phoneNumber: formatDisplayValue(sanitizedInfo.phoneNumber)
                });

                setEditedInfo({
                    userName: sanitizedInfo.userName,
                    email: payload.user_profile_email ? payload.user_profile_email : sanitizedInfo.email,
                    telegramId: sanitizedInfo.telegramId,
                    twitterId: sanitizedInfo.twitterId,
                    twitterName: sanitizedInfo.twitterName,
                    discordId: sanitizedInfo.discordId,
                    discordName: sanitizedInfo.discordName,
                    phoneNumber: sanitizedInfo.phoneNumber
                });

                setIsEditing(false);
                showNotification('success', 'Personal info updated successfully.');
                return true;
            }

            const errorMsg = result?.msg || 'Failed to update personal info. Please try again.';
            showNotification('error', errorMsg);
            return false;
        } catch (error) {
            console.error('Error updating user info:', error);
            showNotification('error', 'Failed to update personal info. Please try again.');
            return false;
        }
    }, [editedInfo, formatDisplayValue, formatEditValue, selectedChain, showNotification, userInfo.email]);

    const handleSaveProfile = useCallback(async () => {
        if (isEmailLogin) {
            await saveUserInfo();
            return;
        }

        const trimmedEmail = (editedInfo.email || '').trim();
        const normalizedNewEmail = normalizeEmailValue(trimmedEmail);
        const baselineEmail = initialEmailDuringEditRef.current || normalizeEmailValue(userInfo.email);
        const emailChanged = Boolean(normalizedNewEmail) && normalizedNewEmail !== baselineEmail;

        if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
            showNotification('error', 'Please enter a valid email address.');
            emailRef.current?.focus();
            return;
        }

        if (walletAddress && emailChanged) {
            setEmailToVerify(trimmedEmail);
            setShowEmailVerificationModal(true);
            return;
        }

        await saveUserInfo({email: trimmedEmail});
    }, [editedInfo.email, emailRegex, isEmailLogin, saveUserInfo, showNotification, userInfo.email, walletAddress]);

    const handleEmailVerificationSuccess = useCallback(async () => {
        const trimmedEmail = emailToVerify.trim();
        setShowEmailVerificationModal(false);

        if (!trimmedEmail) {
            return;
        }

        const success = await saveUserInfo({email: trimmedEmail});
        if (success) {
            setEmailToVerify('');
        } else {
            setIsEditing(true);
            setTimeout(() => {
                emailRef.current?.focus();
            }, 50);
        }
    }, [emailToVerify, saveUserInfo]);

    const handleEdit = async () => {
        if (isEditing) {
            await handleSaveProfile();
        } else {
            // Enter edit mode
            initialEmailDuringEditRef.current = normalizeEmailValue(editedInfo.email);
            setIsEditing(true);
            setTimeout(() => {
                userNameRef.current?.focus();
            }, 50);
        }
    };

    const handleInputChange = (field, value) => {
        setEditedInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getAvatarImage = () => {
        switch (userClass) {
            case 'Amplifier Member':
                return amplifierAvatar;
            case 'Innovator Member':
                return innovatorAvatar;
            default:
                return defaultAvatar;
        }
    };

    const handleDisconnectWallet = async () => {
        try {
            const token = localStorage.getItem('gatewayToken');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const userEmail = localStorage.getItem('userEmail');
            const walletAddress = localStorage.getItem('walletAddress');
            const chain = selectedChain || 'solana';

            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/disconnect-wallet-from-email`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: userEmail,
                    wallet_address: walletAddress,
                    chain
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    // Remove wallet info from local storage and context and reload the page
                    disconnect();
                    localStorage.removeItem('walletAddress');
                    localStorage.removeItem('linkedWalletChain');
                    localStorage.removeItem('lastConnectedWallet');
                    localStorage.removeItem('lastConnectedChain');
                    localStorage.removeItem('selectedChain');
                    setLinkedWalletChain?.('');
                    // Set walletDisconnectSuccess to trigger a notification on page reload
                    localStorage.setItem('walletDisconnectSuccess', true);
                    window.location.reload();
                } else {
                    console.error('Error disconnecting the wallet:', result?.msg)
                    showNotification('error', 'Failed to unlink your wallet. Please try again.')
                }
            }
        } catch (error) {
            console.error('Error disconnecting the wallet:', error);
            showNotification('error', 'Failed to unlink your wallet. Please try again.')
        }
    };

    const handleOpenUpgradeModal = () => {
        setShowUpgradeModal(true);
    };

    const handleCloseUpgradeModal = (upgradeSucceeded = false) => {
        setShowUpgradeModal(false);
        if (upgradeSucceeded) {
            fetchUserInfo();
        }
    };

    const handleCreateUploadApiKey = useCallback(async () => {
        if (!canManageUploadApiKeys) {
            showNotification('warning', 'Only operators can create upload API keys.');
            return;
        }

        const token = localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification('error', 'Authentication token missing. Please log in again.');
            return;
        }

        try {
            setIsCreatingUploadApiKey(true);
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/upload-api-keys`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Failed to create upload API key.');
            }

            setNewUploadApiKey(result.data?.api_key || '');
            setUploadApiKeys((prevKeys) => {
                const createdKey = result.data || null;
                return createdKey ? [createdKey, ...prevKeys] : prevKeys;
            });
            showNotification('success', 'Upload API key created.');
            fetchUploadApiKeys();
        } catch (error) {
            console.error('Error creating upload API key:', error);
            showNotification('error', error.message || 'Failed to create upload API key.');
        } finally {
            setIsCreatingUploadApiKey(false);
        }
    }, [canManageUploadApiKeys, fetchUploadApiKeys, showNotification]);

    const handleCreateDownloadApiKey = useCallback(async () => {
        if (!canManageDownloadApiKeys) {
            showNotification('warning', 'please upgrade plan to access keys.');
            return;
        }

        const token = localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification('error', 'Authentication token missing. Please log in again.');
            return;
        }

        try {
            setIsCreatingDownloadApiKey(true);
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/api-keys`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });

            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Failed to create download API key.');
            }

            setNewDownloadApiKey(result.data?.api_key || '');
            setDownloadApiKeys((prevKeys) => {
                const createdKey = result.data || null;
                return createdKey ? [createdKey, ...prevKeys] : prevKeys;
            });
            showNotification('success', 'Download API key created.');
            fetchDownloadApiKeys();
        } catch (error) {
            console.error('Error creating download API key:', error);
            showNotification('error', error.message || 'Failed to create download API key.');
        } finally {
            setIsCreatingDownloadApiKey(false);
        }
    }, [canManageDownloadApiKeys, fetchDownloadApiKeys, showNotification]);

    const handleRevokeUploadApiKey = useCallback(async (uploadApiKeyId) => {
        const token = localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification('error', 'Authentication token missing. Please log in again.');
            return;
        }

        try {
            setRevokingUploadApiKeyId(uploadApiKeyId);
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/upload-api-keys/${uploadApiKeyId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const message = await getUploadApiKeyErrorMessage(response, 'Failed to revoke upload API key.');
                throw new Error(message);
            }

            setUploadApiKeys((prevKeys) => prevKeys.filter((key) => key.upload_api_key_id !== uploadApiKeyId));
            showNotification('success', 'Upload API key revoked.');
        } catch (error) {
            console.error('Error revoking upload API key:', error);
            showNotification('error', error.message || 'Failed to revoke upload API key.');
        } finally {
            setRevokingUploadApiKeyId(null);
        }
    }, [getUploadApiKeyErrorMessage, showNotification]);

    const handleRevokeDownloadApiKey = useCallback(async (downloadApiKeyId) => {
        const token = localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification('error', 'Authentication token missing. Please log in again.');
            return;
        }

        try {
            setRevokingDownloadApiKeyId(downloadApiKeyId);
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/api-keys/${downloadApiKeyId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const message = await getUploadApiKeyErrorMessage(response, 'Failed to revoke download API key.');
                throw new Error(message);
            }

            setDownloadApiKeys((prevKeys) => prevKeys.filter((key) => key.api_key_id !== downloadApiKeyId));
            showNotification('success', 'Download API key revoked.');
        } catch (error) {
            console.error('Error revoking download API key:', error);
            showNotification('error', error.message || 'Failed to revoke download API key.');
        } finally {
            setRevokingDownloadApiKeyId(null);
        }
    }, [getUploadApiKeyErrorMessage, showNotification]);

    const handleCopyApiKey = useCallback(async (event, apiKeySecret, label = 'API key') => {
        event?.preventDefault();
        event?.stopPropagation();

        if (!apiKeySecret) {
            return;
        }

        try {
            await navigator.clipboard.writeText(apiKeySecret);
            showNotification('success', `${label} copied.`);
        } catch (error) {
            console.error('Error copying API key:', error);
            showNotification('error', `Failed to copy ${label.toLowerCase()}.`);
        }
    }, [showNotification]);

    const formatUploadApiKeyTime = useCallback((value) => {
        if (!value) {
            return 'never used';
        }

        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return 'unknown';
        }

        return parsedDate.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    useEffect(() => {
        if (lastApiKeySessionRef.current === apiKeySessionKey) {
            return;
        }

        lastApiKeySessionRef.current = apiKeySessionKey;
        hasFetchedUploadApiKeysRef.current = false;
        hasFetchedDownloadApiKeysRef.current = false;

        setUploadApiKeys([]);
        setDownloadApiKeys([]);
        setNewUploadApiKey('');
        setNewDownloadApiKey('');
        setIsLoadingUploadApiKeys(false);
        setIsLoadingDownloadApiKeys(false);
        setIsCreatingUploadApiKey(false);
        setIsCreatingDownloadApiKey(false);
        setRevokingUploadApiKeyId(null);
        setRevokingDownloadApiKeyId(null);
    }, [apiKeySessionKey]);

    useEffect(() => {
        if (
            isLoading
            || !hasAuthenticatedSession
            || !canManageUploadApiKeys
            || hasFetchedUploadApiKeysRef.current
        ) {
            return;
        }

        hasFetchedUploadApiKeysRef.current = true;
        fetchUploadApiKeys();
    }, [canManageUploadApiKeys, fetchUploadApiKeys, hasAuthenticatedSession, isLoading]);

    useEffect(() => {
        if (
            isLoading
            || !hasAuthenticatedSession
            || !canManageDownloadApiKeys
            || hasFetchedDownloadApiKeysRef.current
        ) {
            return;
        }

        hasFetchedDownloadApiKeysRef.current = true;
        fetchDownloadApiKeys();
    }, [canManageDownloadApiKeys, fetchDownloadApiKeys, hasAuthenticatedSession, isLoading]);

    const renderLoadingBox = () => (
        <div className={styles.loadingBox}>
            <div className={styles.loadingAvatar}/>
            <div className={styles.loadingTitle}/>
            <div className={styles.loadingContent}>
                <div className={styles.loadingLine}/>
                <div className={styles.loadingLine}/>
                <div className={styles.loadingLine}/>
            </div>
        </div>
    );

    const renderPersonalInfo = () => {
        const hasTwitterName = userInfo.twitterName && userInfo.twitterName !== '--';
        const isTwitterLinked = userInfo.twitterId && userInfo.twitterId !== '--';
        const displayTwitterName = hasTwitterName ? userInfo.twitterName : '--';

        const hasDiscordName = userInfo.discordName && userInfo.discordName !== '--';
        const isDiscordLinked = userInfo.discordId && userInfo.discordId !== '--';
        const displayDiscordName = hasDiscordName ? userInfo.discordName : '--';

        const renderSocialAccount = ({
            isLinked,
            displayName,
            providerName,
            Icon,
            iconClassName,
            isLoading: socialLoading,
            onLink,
            onUnlink,
        }) => {
            if (isLinked) {
                return (
                    <div className={styles.linkedSocialWrap}>
                        <div className={styles.linkedSocialIconFrame}>
                            <div className={styles.socialIconTile}>
                                <Icon className={`${styles.socialBrandIcon} ${iconClassName}`}/>
                            </div>
                            <span className={styles.socialStatusBadge}>
                                <Check size={9} strokeWidth={3} aria-hidden="true"/>
                            </span>
                        </div>
                        <div className={styles.linkedSocialText}>
                            <p>{displayName}</p>
                            <button
                                type="button"
                                className={styles.unlinkSocialButton}
                                onClick={onUnlink}
                            >
                                Unlink
                            </button>
                        </div>
                    </div>
                );
            }

            return (
                <button
                    type="button"
                    className={styles.linkSocialTile}
                    onClick={onLink}
                    disabled={socialLoading}
                    aria-label={`Link ${providerName}`}
                    title={socialLoading ? 'Connecting...' : `Link ${providerName}`}
                >
                    <Icon className={`${styles.socialBrandIcon} ${iconClassName}`}/>
                    <span className={styles.socialStatusBadge}>
                        <Plus size={9} strokeWidth={3} aria-hidden="true"/>
                    </span>
                </button>
            );
        };

        return (
        <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
                <h3>Personal Info</h3>
                <button
                    className={styles.editButton}
                    onClick={handleEdit}
                >
                    {isEditing ? 'Save' : 'Edit'}
                </button>
            </div>

            <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                    <label>User name</label>
                    {isEditing ? (
                        <input
                            ref={userNameRef}
                            type="text"
                            value={editedInfo.userName}
                            onChange={(e) => handleInputChange('userName', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.userName}</span>
                    )}
                </div>

                <div className={`${styles.infoItem}`}>
                    <label>Email</label>
                    {isEditing ? (
                        isEmailLogin ? (
                            <span>{userInfo.email}</span>
                        ) : (
                            <input
                                ref={emailRef}
                                value={editedInfo.email}
                                type="email"
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className={`${styles.infoInput} ${styles.infoInputEmail}`}
                                autoComplete="email"
                            />
                        )
                    ) : (
                        <span>{userInfo.email}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Phone Number</label>
                    {isEditing ? (
                        <input
                            ref={phoneRef}
                            type="tel"
                            value={editedInfo.phoneNumber}
                            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.phoneNumber}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Telegram ID</label>
                    {isEditing ? (
                        <input
                            ref={telegramRef}
                            type="text"
                            value={editedInfo.telegramId}
                            onChange={(e) => handleInputChange('telegramId', e.target.value)}
                            className={styles.infoInput}
                        />
                    ) : (
                        <span>{userInfo.telegramId}</span>
                    )}
                </div>

                <div className={styles.infoItem}>
                    <label>Twitter</label>
                    {renderSocialAccount({
                        isLinked: isTwitterLinked,
                        displayName: displayTwitterName,
                        providerName: 'Twitter',
                        Icon: TwitterXIcon,
                        iconClassName: styles.twitterBrandIcon,
                        isLoading: isLinkingTwitter,
                        onLink: handleTwitterLink,
                        onUnlink: handleTwitterUnlink,
                    })}
                </div>
                
                <div className={styles.infoItem}>
                    <label>Discord</label>
                    {renderSocialAccount({
                        isLinked: isDiscordLinked,
                        displayName: displayDiscordName,
                        providerName: 'Discord',
                        Icon: DiscordIcon,
                        iconClassName: styles.discordBrandIcon,
                        isLoading: isLinkingDiscord,
                        onLink: handleDiscordLink,
                        onUnlink: handleDiscordUnlink,
                    })}
                </div>

            </div>
        </div>
    );
    };

    const getMembershipTier = () => {
        if (userClass === 'Innovator Member') {
            return 'innovator';
        }
        if (userClass === 'Amplifier Member') {
            return 'amplifier';
        }
        return 'explorer';
    };

    const getMembershipStatus = (tier) => {
        const rank = {
            explorer: 0,
            amplifier: 1,
            innovator: 2,
        };
        const delta = rank[tier] - rank[getMembershipTier()];
        if (delta < 0) {
            return 'included';
        }
        return delta === 0 ? 'current' : 'upgrade';
    };

    const renderIncludedMark = (className) => (
        <span className={`${styles.tierIncludedMark} ${className}`}>
            <Check size={11} strokeWidth={3} aria-hidden="true"/>
            Included
        </span>
    );

    const renderTierPerks = (perks, accentClassName, muted = false) => (
        <ul className={styles.tierPerks}>
            {perks.map((perk) => (
                <li key={perk}>
                    <Check
                        size={14}
                        strokeWidth={2.4}
                        className={muted ? styles.tierMutedCheck : accentClassName}
                        aria-hidden="true"
                    />
                    {perk}
                </li>
            ))}
        </ul>
    );

    const renderCommunityMembershipSection = () => {
        const explorerStatus = getMembershipStatus('explorer');
        const amplifierStatus = getMembershipStatus('amplifier');
        const innovatorStatus = getMembershipStatus('innovator');
        const isExplorerMembership = getMembershipTier() === 'explorer';

        return (
            <section className={styles.communitySection}>
                <div className={styles.communityHeader}>
                    <div className={styles.communityTitleRow}>
                        <UsersGroupIcon className={styles.communityIcon}/>
                        <h3>Community</h3>
                    </div>
                    <p>Contribute data, earn Prisma points, unlock fleet access.</p>
                </div>

                <div className={styles.tierGrid}>
                    <div className={`${styles.tierCard} ${explorerStatus === 'current' ? styles.tierCardCurrentExplorer : ''}`}>
                        <div className={styles.tierCardHeader}>
                            <span className={`${styles.tierName} ${styles.explorerTierName}`}>Explorer</span>
                            {explorerStatus === 'current' ? (
                                <span className={styles.explorerCurrentBadge}>CURRENT</span>
                            ) : renderIncludedMark(styles.explorerIncludedMark)}
                        </div>
                        {renderTierPerks(['Contribute and earn Prisma points'], styles.explorerCheck)}
                    </div>

                    <div className={`${styles.tierCard} ${styles.amplifierTierCard}`}>
                        <div className={styles.tierCardHeader}>
                            <span className={styles.tierTitleWithIcon}>
                                {amplifierStatus === 'current' && (
                                    <Crown
                                        size={16}
                                        strokeWidth={2}
                                        className={`${styles.tierTitleIcon} ${styles.amplifierCheck}`}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className={`${styles.tierName} ${styles.amplifierTierName}`}>Amplifier</span>
                            </span>
                            {amplifierStatus === 'current' ? (
                                <span className={styles.amplifierCurrentBadge}>CURRENT</span>
                            ) : amplifierStatus === 'included' ? (
                                renderIncludedMark(styles.amplifierIncludedMark)
                            ) : null}
                        </div>
                        {amplifierStatus === 'upgrade' ? (
                            <p className={styles.tierPrice}>$99 <small>one-time initiation fee</small></p>
                        ) : (
                            <p className={styles.tierActivated}>Activated · paid $99</p>
                        )}
                        {renderTierPerks(
                            ['VLA Foundry access', 'Robot fleet access', 'Enhanced Prisma earning (3x speed)'],
                            styles.amplifierCheck,
                            amplifierStatus === 'upgrade'
                        )}
                        {amplifierStatus === 'upgrade' && (
                            <button
                                type="button"
                                className={styles.amplifierUpgradeButton}
                                onClick={handleOpenUpgradeModal}
                            >
                                Upgrade — $99
                            </button>
                        )}
                    </div>

                    <div className={`${styles.tierCard} ${styles.innovatorTierCard}`}>
                        <div className={styles.tierCardHeader}>
                            <span className={styles.tierTitleWithIcon}>
                                {innovatorStatus === 'current' && (
                                    <Diamond
                                        size={16}
                                        strokeWidth={2}
                                        className={`${styles.tierTitleIcon} ${styles.innovatorCheck}`}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className={`${styles.tierName} ${styles.innovatorTierName}`}>Innovator</span>
                            </span>
                            {innovatorStatus === 'current' && (
                                <span className={styles.innovatorCurrentBadge}>CURRENT</span>
                            )}
                        </div>
                        {innovatorStatus === 'current' ? (
                            <p className={styles.tierActivated}>Activated · paid $399</p>
                        ) : (
                            <p className={styles.tierPrice}>$399 <small>one-time initiation fee</small></p>
                        )}
                        {renderTierPerks(
                            ['Fast-track live control queue', 'Priority robot fleet access', 'Ultra Prisma earning (5x speed)'],
                            styles.innovatorCheck,
                            innovatorStatus === 'upgrade'
                        )}
                        {innovatorStatus === 'upgrade' && (
                            <button
                                type="button"
                                className={`${styles.innovatorUpgradeButton} ${isExplorerMembership ? styles.upgradeButtonDisabled : ''}`}
                                onClick={handleOpenUpgradeModal}
                                disabled={isExplorerMembership}
                                title={isExplorerMembership ? 'Upgrade to Amplifier first' : 'Upgrade to Innovator'}
                            >
                                Upgrade — $399
                            </button>
                        )}
                    </div>
                </div>
            </section>
        );
    };


    const renderEmailWalletAddressSection = () => {
        const email = localStorage.getItem('userEmail');
        const walletAddress = localStorage.getItem('walletAddress');
        const selectedChain = localStorage.getItem('linkedWalletChain') || localStorage.getItem('selectedChain');
        const showDisconnectWalletOption = !!(email && walletAddress);

        const handleTooltipHoverChange = (isHovered) => {
            setIsHoveredDisconnectWallet(isHovered);
        };

        let emailChainIconSrc;
        if (walletAddress) {
            if (selectedChain === 'solana') emailChainIconSrc = solanaChainIcon;
            else if (selectedChain === 'ethereum') emailChainIconSrc = ethChainIcon;
            else if (selectedChain === 'base') emailChainIconSrc = baseChainIcon;
            else if (selectedChain === 'monad') emailChainIconSrc = monadChainIcon;
            else if (selectedChain === 'aptos') emailChainIconSrc = aptosChainIcon;
        } else if (email) {
            emailChainIconSrc = emailIcon;
        }

        let userProfileAddress;
        if (walletAddress) {
            userProfileAddress = shortenAddress(walletAddress)
        } else if (email) {
            userProfileAddress = (isSmallMobile || email.length > 20) ? shortenAddress(email) : email;
        } else {
            userProfileAddress = '--';   
        }

        return (
               <div className={styles.userWalletSection}>
                    {emailChainIconSrc && (
                        <img src={emailChainIconSrc} className={styles.emailChainIcon} alt="Email or chain user profile icon" />
                    )}
                    <h2 className={styles.userName}>{userProfileAddress}</h2>
                    {showDisconnectWalletOption && (
                        <TooltipBottom 
                            text="Unlink Wallet"
                            enableTooltipHoverEffect
                            onHoverChange={handleTooltipHoverChange}
                        >
                            <div className={styles.disconnectIconContainer}>
                                    <img
                                        src={isHoveredDisconnectWallet ? disconnectOrangeTopaz : disconnectOrangeCamel}
                                        alt="Unlink"
                                        onClick={handleDisconnectWallet}
                                    />
                            </div>   
                        </TooltipBottom>
                    )}
                </div>
        )
    };

    const renderUploadApiKeysSection = () => {
        const renderApiKeyGroup = ({
            type,
            title,
            description,
            tag,
            planTag,
            keys,
            newKey,
            canManage,
            isLoadingKeys,
            isCreatingKey,
            noAccessMessage,
            onCreate,
            onRevoke,
            revokingKeyId,
            getApiKeyId,
            Icon,
        }) => {
            const visibleKeys = keys.filter((apiKey) => String(apiKey.status || '').toUpperCase() === 'ACTIVE');
            const isSubscriber = type === 'subscriber';

            return (
                <div className={`${styles.apiKeyGroup} ${isSubscriber ? styles.apiKeyGroupSubscriber : styles.apiKeyGroupOperator}`}>
                    <div className={styles.apiKeyGroupHead}>
                        <div className={styles.apiKeyGroupTitleBlock}>
                            <Icon
                                size={20}
                                strokeWidth={2.4}
                                className={isSubscriber ? styles.apiKeyGroupIconSubscriber : styles.apiKeyGroupIconOperator}
                                aria-hidden="true"
                            />
                            <div>
                                <div className={styles.apiKeyGroupTitleLine}>
                                    <span className={styles.apiKeyGroupName}>{title}</span>
                                    <span className={`${styles.apiKeyGroupTag} ${isSubscriber ? styles.apiKeyGroupTagDownload : styles.apiKeyGroupTagUpload}`}>
                                        {tag}
                                    </span>
                                    {planTag && (
                                        <span className={styles.apiKeyGroupPlanTag}>{planTag}</span>
                                    )}
                                </div>
                                <p className={styles.apiKeyGroupDescription}>
                                    {description}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.createApiKeyButton}
                            onClick={onCreate}
                            disabled={!canManage || isCreatingKey}
                            title={canManage ? `Create ${type} API key` : undefined}
                        >
                            <Plus size={18} strokeWidth={2.4} aria-hidden="true"/>
                            {isCreatingKey ? 'Creating...' : 'New key'}
                        </button>
                    </div>

                    {isSubscriber && (
                        <div className={styles.apiKeyQuotaStrip}>
                            <div>
                                <p className={styles.apiKeyQuotaLabel}>Plan quota</p>
                                <p className={styles.apiKeyQuotaValue}>500 GB / mo</p>
                            </div>
                            <div>
                                <p className={styles.apiKeyQuotaLabel}>Used</p>
                                <p className={styles.apiKeyQuotaValue}>210 GB</p>
                            </div>
                            <div>
                                <p className={styles.apiKeyQuotaLabel}>Rate</p>
                                <p className={styles.apiKeyQuotaValue}>100 req/min</p>
                            </div>
                            <button type="button" className={styles.apiKeyUpgradeLink} onClick={handleOpenUpgradeModal}>
                                Upgrade plan ->
                            </button>
                        </div>
                    )}

                    {newKey && (
                        <div className={styles.apiKeyReveal}>
                            <div>
                                <span className={styles.apiKeyRevealLabel}>New {type} API key</span>
                                <code className={styles.apiKeySecret}>{newKey}</code>
                            </div>
                            <button
                                type="button"
                                className={styles.copyApiKeyButton}
                                onClick={(event) => handleCopyApiKey(event, newKey, `${type} API key`)}
                                aria-label={`Copy ${type} API key`}
                            >
                                <Copy size={18} strokeWidth={2.2} aria-hidden="true"/>
                            </button>
                        </div>
                    )}

                    <div className={styles.apiKeysList}>
                        {!canManage ? (
                            <div className={styles.apiKeyEmpty}>{noAccessMessage}</div>
                        ) : isLoadingKeys ? (
                            <div className={styles.apiKeyEmpty}>Loading {type} API keys...</div>
                        ) : visibleKeys.length === 0 ? (
                            <div className={styles.apiKeyEmpty}>No {type} API keys yet.</div>
                        ) : (
                            visibleKeys.map((apiKey) => {
                                const apiKeyId = getApiKeyId(apiKey);

                                return (
                                    <div className={styles.apiKeyRow} key={`${type}-${apiKeyId}`}>
                                        <div className={styles.apiKeyMain}>
                                            <div className={styles.apiKeyTitleLine}>
                                                <span className={styles.apiKeyStatusDot}/>
                                                <span className={styles.apiKeyName}>{apiKey.name || `${type}-key`}</span>
                                                <code className={styles.apiKeyPrefix}>{apiKey.key_prefix}...</code>
                                            </div>
                                            <div className={styles.apiKeyMeta}>
                                                last used {formatUploadApiKeyTime(apiKey.last_used_at)}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.revokeApiKeyButton}
                                            onClick={() => onRevoke(apiKeyId)}
                                            disabled={revokingKeyId === apiKeyId}
                                        >
                                            {revokingKeyId === apiKeyId ? 'Revoking...' : 'Revoke'}
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            );
        };

        return (
            <section className={styles.apiKeysSection}>
                <div className={styles.apiKeysHeader}>
                    <div className={styles.apiKeysTitleRow}>
                        <KeyIcon className={styles.apiKeysIcon}/>
                        <h3>API Keys</h3>
                    </div>
                    <p className={styles.apiKeysSubtitle}>
                        Keys are grouped by role. Each role has its own access and limits, set by your account.
                    </p>
                </div>

                <div className={styles.apiKeyGroups}>
                    {renderApiKeyGroup({
                        type: 'subscriber',
                        title: 'Subscriber',
                        description: 'Download episode data to train models',
                        tag: 'DOWNLOAD',
                        planTag: 'Researcher plan',
                        keys: downloadApiKeys,
                        newKey: newDownloadApiKey,
                        canManage: canManageDownloadApiKeys,
                        isLoadingKeys: isLoadingDownloadApiKeys,
                        isCreatingKey: isCreatingDownloadApiKey,
                        noAccessMessage: 'please upgrade plan to access keys.',
                        onCreate: handleCreateDownloadApiKey,
                        onRevoke: handleRevokeDownloadApiKey,
                        revokingKeyId: revokingDownloadApiKeyId,
                        getApiKeyId: (apiKey) => apiKey.api_key_id,
                        Icon: Download,
                    })}

                    {renderApiKeyGroup({
                        type: 'operator',
                        title: 'Operator',
                        description: 'Upload episodes from teleoperation stations',
                        tag: 'UPLOAD',
                        keys: uploadApiKeys,
                        newKey: newUploadApiKey,
                        canManage: canManageUploadApiKeys,
                        isLoadingKeys: isLoadingUploadApiKeys,
                        isCreatingKey: isCreatingUploadApiKey,
                        noAccessMessage: 'Only operators can create upload API keys.',
                        onCreate: handleCreateUploadApiKey,
                        onRevoke: handleRevokeUploadApiKey,
                        revokingKeyId: revokingUploadApiKeyId,
                        getApiKeyId: (apiKey) => apiKey.upload_api_key_id,
                        Icon: Gamepad2,
                    })}
                </div>
            </section>
        );
    };

    return (
        <div className={styles.accountContainer}>
            <div className={styles.accountContent}>
                {!isLoading && (<div className={styles.profileSection}>
                    <img
                        src={getAvatarImage()}
                        alt="User Avatar"
                        className={styles.avatar}
                    />
                    {renderEmailWalletAddressSection()}
                </div>)}


                {isLoading ? (
                    renderLoadingBox()
                ) : (
                    <>
                        {renderPersonalInfo()}

                        {renderCommunityMembershipSection()}

                        <div className={styles.operatorSection}>
                            <div className={styles.operatorSectionHeader}>
                                <div className={styles.operatorSectionTitleRow}>
                                    <ToolIcon className={styles.operatorSectionIcon}/>
                                    <h3>VLA Foundry</h3>
                                </div>
                                <p>Operate robots to complete real-world tasks and earn rewards.</p>
                            </div>
                            <OperatorSection
                                autoOpenRegisterModal={shouldOpenOperatorRegisterModal}
                                userProfileEmail={(userInfo.email !== '--') ? userInfo.email : ''}
                                onUpdateUserProfileEmail = {(email) => {
                                    setUserInfo({
                                        ...userInfo,
                                        email: formatDisplayValue(email)
                                    });
                                    setEditedInfo({
                                        ...editedInfo,
                                        email: formatEditValue(email)
                                    });
                                }}
                            />
                        </div>
                        {renderUploadApiKeysSection()}
                    </>
                )}
            </div>
            {showUpgradeModal && (
                <UpgradeMembershipModal
                    onClose={handleCloseUpgradeModal}
                    onGoBack={() => setShowUpgradeModal(false)}
                />
            )}
            {showEmailVerificationModal && emailToVerify && (
                <EmailVerificationModal
                    email={emailToVerify}
                    onClose={() => {
                        setShowEmailVerificationModal(false);
                        setEmailToVerify('');
                    }}
                    onChangeEmail={() => {
                        setShowEmailVerificationModal(false);
                        setEmailToVerify('');
                        setIsEditing(true);
                        setTimeout(() => {
                            emailRef.current?.focus();
                        }, 50);
                    }}
                    userId={userId}
                    onVerified={handleEmailVerificationSuccess}
                />
            )}
        </div>
    );
};

export default Account;
